import { Check, RefreshCw, Sparkles, X, Zap } from 'lucide-react';
import type { MouseEvent } from 'react';
import { AsyncActionBtn } from '@/components/ui/AsyncActionBtn';
import { HoverTip } from '@/components/ui/HoverTip';
import { useApplicationAction } from '@/hooks/useApplicationAction';
import { useRetailor } from '@/hooks/useRetailor';
import { useScoreJob } from '@/hooks/useScoreJob';
import type { Job } from '@/types/job.type';

interface JobRowActionsProps {
  job: Job;
  variant: 'compact' | 'comfortable';
}

export const JobRowActions = ({
  job: { id, status, applicationId },
  variant,
}: JobRowActionsProps) => {
  const applicationAction = useApplicationAction();
  const retailor = useRetailor();
  const scoreJob = useScoreJob();

  const hasApplication = applicationId !== null;
  const isReady = status === 'ready';
  const isScored = status === 'scored';
  const isApproved = status === 'approved';
  const isNew = status === 'new';
  const isScoreFailed = status === 'score_failed';

  const stopPropagation = (event: MouseEvent) => event.stopPropagation();

  if (isReady && hasApplication) {
    const id = applicationId ?? 0;

    const approveSync = (event: MouseEvent) => {
      stopPropagation(event);

      applicationAction.mutate({
        action: 'approve',
        applicationId: id,
      });
    };

    const rejectSync = (event: MouseEvent) => {
      stopPropagation(event);

      applicationAction.mutate({
        action: 'reject',
        applicationId: id,
      });
    };

    const approveAsync = () =>
      applicationAction.mutateAsync({
        action: 'approve',
        applicationId: id,
      });

    const rejectAsync = () =>
      applicationAction.mutateAsync({
        action: 'reject',
        applicationId: id,
      });

    if (variant === 'compact') {
      return (
        <div className="flex justify-end gap-0.5">
          <HoverTip placement="top" text="Approve">
            <button
              aria-label="Approve job"
              className="size-6 rounded-sm text-score-high hover:bg-score-high/15"
              onClick={approveSync}
              type="button"
            >
              <Check className="mx-auto size-3.5" />
            </button>
          </HoverTip>
          <HoverTip placement="top" text="Reject">
            <button
              aria-label="Reject job"
              className="size-6 rounded-sm text-score-low hover:bg-score-low/15"
              onClick={rejectSync}
              type="button"
            >
              <X className="mx-auto size-3.5" />
            </button>
          </HoverTip>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1">
        <AsyncActionBtn
          icon={Check}
          onAction={approveAsync}
          onClick={stopPropagation}
          size="xs"
          variant="success"
        >
          Approve
        </AsyncActionBtn>
        <AsyncActionBtn
          icon={X}
          onAction={rejectAsync}
          onClick={stopPropagation}
          size="xs"
          variant="danger"
        />
      </div>
    );
  }

  if (variant === 'compact') {
    return <div />;
  }

  if (isScored || isApproved) {
    const retailorAsync = () => retailor.mutateAsync(id);

    return (
      <div className="flex items-center gap-1">
        <AsyncActionBtn
          animation={!hasApplication ? 'pulse' : undefined}
          icon={hasApplication ? RefreshCw : Sparkles}
          onAction={retailorAsync}
          onClick={stopPropagation}
          size="xs"
          variant="soft"
        >
          {hasApplication ? 'Retailor' : 'Tailor'}
        </AsyncActionBtn>
      </div>
    );
  }

  if (isNew || isScoreFailed) {
    const scoreAsync = () => scoreJob.mutateAsync(id);

    return (
      <div className="flex items-center gap-1">
        <AsyncActionBtn
          animation={isNew ? 'pulse' : undefined}
          icon={isNew ? Zap : RefreshCw}
          onAction={scoreAsync}
          onClick={stopPropagation}
          size="xs"
          variant={isNew ? 'soft' : 'outline'}
        >
          Score
        </AsyncActionBtn>
      </div>
    );
  }

  return <div />;
};
